/**
 * SSE Parser Utility
 *
 * Parses raw text/event-stream chunks from ReadableStream into structured frames.
 * Handles partial chunks (buffers across read() calls), multi-line data fields,
 * and comment lines (starting with ":").
 *
 * Reference: https://html.spec.whatwg.org/multipage/server-sent-events.html#parsing-an-event-stream
 */

import type { RawSSEFrame } from '@/types/stream.types';

// ---------------------------------------------------------------------------
// SSEParser class (stateful across chunks)
// ---------------------------------------------------------------------------

export class SSEParser {
  private buffer = '';
  private currentEvent = '';
  private currentData: string[] = [];
  private currentId: string | undefined = undefined;

  /**
   * Feed a raw text chunk from the ReadableStream reader.
   * Returns an array of complete SSE frames extracted from this chunk.
   * Incomplete events are buffered for the next call.
   */
  feed(chunk: string): RawSSEFrame[] {
    this.buffer += chunk;
    const frames: RawSSEFrame[] = [];

    // Process complete lines (terminated by \n, \r\n, or \r)
    while (true) {
      const lineEnd = this.findLineEnd();
      if (lineEnd === -1) break;

      const line = this.buffer.slice(0, lineEnd);
      const nextStart = this.computeNextStart(lineEnd);
      this.buffer = this.buffer.slice(nextStart);

      if (line === '') {
        // Empty line = event boundary
        if (this.currentData.length > 0) {
          frames.push({
            event: this.currentEvent || 'message',
            data: this.currentData.join('\n'),
            id: this.currentId,
          });
        }
        this.resetCurrent();
      } else {
        this.parseLine(line);
      }
    }

    return frames;
  }

  /**
   * Reset internal state. Call when reconnecting to a new stream.
   */
  reset(): void {
    this.buffer = '';
    this.resetCurrent();
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  private findLineEnd(): number {
    // Find the earliest line terminator
    const nIdx = this.buffer.indexOf('\n');
    const rIdx = this.buffer.indexOf('\r');

    if (nIdx === -1 && rIdx === -1) return -1;
    if (nIdx === -1) return rIdx;
    if (rIdx === -1) return nIdx;
    return Math.min(nIdx, rIdx);
  }

  private computeNextStart(lineEnd: number): number {
    // Handle \r\n as a single line terminator
    if (
      this.buffer[lineEnd] === '\r' &&
      this.buffer[lineEnd + 1] === '\n'
    ) {
      return lineEnd + 2;
    }
    return lineEnd + 1;
  }

  private parseLine(line: string): void {
    // Comment lines (start with ":") are ignored
    if (line.startsWith(':')) return;

    const colonIdx = line.indexOf(':');

    if (colonIdx === -1) {
      // Field with no value (e.g., "data" without ":")
      this.processField(line, '');
      return;
    }

    const field = line.slice(0, colonIdx);
    // Per spec: if the first character after ":" is a space, it is removed
    let value = line.slice(colonIdx + 1);
    if (value.startsWith(' ')) {
      value = value.slice(1);
    }

    this.processField(field, value);
  }

  private processField(field: string, value: string): void {
    switch (field) {
      case 'event':
        this.currentEvent = value;
        break;
      case 'data':
        this.currentData.push(value);
        break;
      case 'id':
        // Per spec, id field must not contain null character
        if (!value.includes('\0')) {
          this.currentId = value;
        }
        break;
      case 'retry':
        // Retry field is handled at connection level, not parsed here
        break;
      default:
        // Unknown fields are ignored per spec
        break;
    }
  }

  private resetCurrent(): void {
    this.currentEvent = '';
    this.currentData = [];
    this.currentId = undefined;
  }
}
